import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, CheckCircle2, AlertTriangle, HelpCircle, Square, CheckSquare, RotateCcw } from "lucide-react";

// Phase 3 · Additive availability pill next to each sticker code.
// Zero effect on pricing, counts or ordering — informational only.
function AvailabilityPill({ availability }) {
  if (!availability) return null;
  const s = availability.availability_status;
  if (s === "in_stock") {
    return (
      <span
        className="inline-flex items-center gap-0.5 rounded-full border border-emerald-800 bg-emerald-50 text-emerald-900 px-1.5 py-0 text-[9px] font-bold uppercase tracking-widest"
        data-testid="quote-avail-in-stock"
        title="Currently in stock"
      >
        <CheckCircle2 size={9} /> In Stock
      </span>
    );
  }
  if (s === "out_of_stock") {
    return (
      <span
        className="inline-flex items-center gap-0.5 rounded-full border border-amber-800 bg-amber-50 text-amber-900 px-1.5 py-0 text-[9px] font-bold uppercase tracking-widest"
        data-testid="quote-avail-out-of-stock"
        title="Currently out of stock — you may keep or remove at checkout in a future update."
      >
        <AlertTriangle size={9} /> Currently Out of Stock
      </span>
    );
  }
  if (s === "not_initialized") {
    return (
      <span
        className="inline-flex items-center gap-0.5 rounded-full border border-neutral-500 bg-neutral-100 text-neutral-700 px-1.5 py-0 text-[9px] font-bold uppercase tracking-widest"
        data-testid="quote-avail-not-initialized"
        title="Availability has not yet been confirmed"
      >
        <HelpCircle size={9} /> Availability Not Confirmed
      </span>
    );
  }
  return null;
}

/**
 * QuoteCategorySummary — collapsible category grouping for the quote
 * summary shown on both the public Missing Stickers page and the
 * Admin Manual Order Entry preview.
 *
 * Grouping is DERIVED from `display_category` on each priced line
 * (backend-classified against the Master Sticker Database — see
 * `order_services/pricing_engine._classify_display_category`). The
 * frontend never invents categories, never hardcodes player names,
 * and never touches unit_price / line_total.
 *
 * Behaviour contract (owner directive):
 *   • Fixed section order: FWC → Team Badges → Special Players →
 *     Coca-Cola → $2 Stickers → $1 Stickers → Regular Stickers → Other.
 *   • Categories with ≤ 5 items default OPEN. > 5 items default
 *     COLLAPSED. Multiple categories may be open simultaneously.
 *   • Empty categories are hidden entirely.
 *   • Line-level fields (code, qty, unit_price, line_total) are
 *     rendered verbatim — no recalculation.
 *
 * `variant` picks the visual treatment:
 *   • "customer" — big yellow-strip header (public review quote).
 *   • "admin"    — compact grey header (manual order entry preview).
 */

// Fixed order and label mapping — matches backend
// _classify_display_category. Frontend NEVER derives its own labels.
export const QUOTE_CATEGORY_ORDER = [
  { key: "fwc",            label: "FWC" },
  { key: "team_badge",     label: "Team Badges" },
  { key: "special_player", label: "Special Players" },
  { key: "coca_cola",      label: "Coca-Cola" },
  { key: "two_dollar",     label: "$2 Stickers" },
  { key: "one_dollar",     label: "$1 Stickers" },
  { key: "regular",        label: "Regular Stickers" },
  { key: "other",          label: "Other" },
];

// Gold Crumple · price-driven category ordering. Static buckets
// (FWC / Team Badges / Premium / Coca-Cola) come from the backend
// classifier; every other Gold line lands in a dynamic
// `gold_price_{cents}` bucket labelled "$X Stickers" — sorted
// highest → lowest so the customer always sees the priciest tier
// first. Only buckets that actually appear in this snapshot are
// rendered; nothing is invented client-side.
export const GOLD_STATIC_ORDER_HEAD = ["fwc", "team_badge", "premium_over_100"];
export const GOLD_STATIC_ORDER_TAIL = ["coca_cola"];
// Defensive: if a legacy line arrives with a `regular` key on a Gold
// snapshot (e.g. missing master price), keep it visible at the very
// bottom so nothing gets silently dropped.
const GOLD_STATIC_FALLBACK = ["regular"];

function _gold_price_bucket_cents(key) {
  const m = String(key || "").match(/^gold_price_(\d+)$/);
  return m ? parseInt(m[1], 10) : null;
}

function _build_gold_order(presentKeys) {
  // Extract every dynamic `gold_price_{cents}` key from the snapshot
  // and sort them descending by cents. All other keys are matched
  // against the static head / tail lists in owner-approved order.
  const dynamicKeys = presentKeys
    .filter((k) => _gold_price_bucket_cents(k) !== null)
    .sort((a, b) => _gold_price_bucket_cents(b) - _gold_price_bucket_cents(a));
  const order = [];
  for (const k of GOLD_STATIC_ORDER_HEAD) {
    if (presentKeys.includes(k)) order.push(k);
  }
  for (const k of dynamicKeys) order.push(k);
  for (const k of GOLD_STATIC_ORDER_TAIL) {
    if (presentKeys.includes(k)) order.push(k);
  }
  for (const k of GOLD_STATIC_FALLBACK) {
    if (presentKeys.includes(k) && !order.includes(k)) order.push(k);
  }
  return order;
}

const OTHER_KEY = "other";
const OTHER_LABEL = "Other";

export function groupLinesByCategory(lines, isSelected, orderVariant) {
  const isGold = orderVariant === "gold_crumple";
  // Bucket every line by its backend-supplied display_category key.
  // Labels are ALSO backend-supplied — the frontend never invents a
  // Gold price-bucket heading.
  const buckets = new Map();
  const labelByKey = new Map();
  const staticKeys = new Set(QUOTE_CATEGORY_ORDER.map((c) => c.key));
  for (const c of QUOTE_CATEGORY_ORDER) {
    buckets.set(c.key, []);
    labelByKey.set(c.key, c.label);
  }
  for (const ln of (lines || [])) {
    let key = ln.display_category;
    // Defensive fallbacks:
    //   • White/Blue snapshot with an unknown key → `other`.
    //   • Gold snapshot with an unknown key → `regular` (never `other`,
    //     never a White dollar-tier heading).
    if (!key) key = isGold ? "regular" : "other";
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(ln);
    // Record the backend-supplied label so headings for dynamic Gold
    // price buckets ("$40 Stickers") render exactly as the snapshot
    // declared them.
    const backendLabel = ln.display_category_label;
    if (backendLabel && !labelByKey.has(key)) {
      labelByKey.set(key, backendLabel);
    } else if (backendLabel && !staticKeys.has(key)) {
      // Dynamic (Gold price) key — always trust the backend label.
      labelByKey.set(key, backendLabel);
    }
  }
  // Build the ordered list of keys we will actually render.
  const presentKeys = Array.from(buckets.keys()).filter(
    (k) => (buckets.get(k) || []).length > 0);
  let orderedKeys = isGold
    ? _build_gold_order(presentKeys)
    : QUOTE_CATEGORY_ORDER.map((c) => c.key).filter(
        (k) => presentKeys.includes(k));
  // F5 (owner directive 2026-02-20) · Safety net — never silently
  // drop a priced line from render. If any present key is missing
  // from the ordered list (e.g. a Gold `gold_price_*` bucket
  // survived on a snapshot rendered under `variant="white"`), tack
  // it on the end so the customer still sees the row. The line's
  // `display_category_label` (baked in via `labelByKey` above)
  // provides the human-readable heading. This is defence-in-depth
  // — normal single-variant rendering is unchanged.
  const orderedSet = new Set(orderedKeys);
  const unmatchedKeys = presentKeys.filter((k) => !orderedSet.has(k));
  if (unmatchedKeys.length > 0) {
    orderedKeys = [...orderedKeys, ...unmatchedKeys];
  }

  // If a selection predicate is provided, aggregate qty + subtotal from
  // ONLY the currently-checked lines. Otherwise aggregate everything.
  const sel = typeof isSelected === "function" ? isSelected : () => true;
  const groups = [];
  for (const key of orderedKeys) {
    const bucket = buckets.get(key) || [];
    if (bucket.length === 0) continue;
    const label = labelByKey.get(key) || key;
    // Deterministic sort inside each bucket so the same lines always
    // render in the same order across renders. Uses canonical album/
    // sticker-code ordering (numeric-aware), not player-name sort.
    const sorted = [...bucket].sort((a, b) =>
      String(a.code).localeCompare(String(b.code), undefined, { numeric: true })
    );
    const checked = sorted.filter((l) => sel(l.code));
    groups.push({
      key,
      label,
      lines: sorted,
      // Phase 4 · qty + subtotal reflect only currently-checked lines.
      // Full line list is still rendered so the customer can restore
      // individual items via their checkbox.
      qty: checked.reduce((s, l) => s + Number(l.qty || 0), 0),
      subtotal: checked.reduce((s, l) => s + Number(l.line_total || 0), 0),
      full_qty: sorted.reduce((s, l) => s + Number(l.qty || 0), 0),
    });
  }
  return groups;
}

function CategoryBlock({ group, variant, defaultOpen, selectionApi }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const headerCls =
    variant === "admin"
      ? "w-full flex items-center justify-between px-3 py-2 border-b-2 border-[var(--ink)] bg-gray-50 hover:bg-gray-100 transition text-left"
      : "w-full flex items-center justify-between px-5 py-3 border-b-4 border-[var(--ink)] bg-[var(--panini-yellow-soft)] hover:bg-[var(--panini-yellow)]/40 transition text-left";
  const wrapperCls =
    variant === "admin"
      ? "rounded-lg border-2 border-[var(--ink)] bg-white overflow-hidden"
      : "rounded-2xl border-4 border-[var(--ink)] bg-white overflow-hidden";
  const titleCls =
    variant === "admin"
      ? "font-bold text-sm md:text-base truncate"
      : "font-display text-base md:text-lg truncate";
  const totalCls =
    variant === "admin"
      ? "font-mono font-bold text-sm md:text-base shrink-0 tabular-nums"
      : "font-mono font-bold text-base md:text-lg shrink-0 tabular-nums";
  const rowCls =
    variant === "admin"
      ? "grid grid-cols-12 gap-3 px-3 py-1.5 text-sm items-center"
      : "grid grid-cols-12 gap-3 px-5 py-2 text-sm items-center";
  const linesWrapCls =
    variant === "admin"
      ? "divide-y divide-gray-100"
      : "divide-y divide-[var(--stone)]";

  const sel = selectionApi || null;
  const isRowSelected = (code) => (sel ? sel.isSelected(code) : true);
  // Missing-Sticker Ordering · Group Controls — compute the tri-state
  // "selected/none/indeterminate" per category so the header can show
  // a Select All / Unselect All control that reflects the current
  // group state.
  const groupSelectedCount = sel
    ? group.lines.filter((l) => sel.isSelected(l.code)).length
    : group.lines.length;
  const groupTotal = group.lines.length;
  const groupNoneSelected = sel ? groupSelectedCount === 0 : false;
  const groupAllSelected = groupSelectedCount === groupTotal;
  const groupIndeterminate = !groupNoneSelected && !groupAllSelected;
  // Phase 5 · per-category Restore action — only meaningful in customer
  // variant when some rows in this group are currently deselected.
  const groupHasDeselected = sel
    ? group.lines.some((l) => !sel.isSelected(l.code))
    : false;
  const showRestoreButton =
    variant === "customer" &&
    !!sel?.onRestoreCategory &&
    groupHasDeselected;
  const showSelectAll =
    variant === "customer" && !!sel?.onSelectCategory && !groupAllSelected;
  const showUnselectAll =
    variant === "customer" && !!sel?.onUnselectCategory && !groupNoneSelected;
  // Group-level "toggle all in this category" — helper for future.
  // Not exposed as a control in v1 to keep the UI focused.

  return (
    <div className={wrapperCls} data-testid={`quote-category-${group.key}`}>
      <div className="w-full flex items-stretch">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={headerCls + " flex-1"}
          data-testid={`quote-category-toggle-${group.key}`}
          aria-expanded={open}
        >
          <div className="flex items-center gap-2 min-w-0">
            {open
              ? <ChevronDown size={variant === "admin" ? 16 : 18} className="shrink-0" />
              : <ChevronRight size={variant === "admin" ? 16 : 18} className="shrink-0" />}
            <div className="min-w-0">
              <div className={titleCls} data-testid={`quote-category-label-${group.key}`}>
                {group.label}{" "}
                <span className="text-xs font-normal text-[var(--smoke)]">
                  ({group.qty}{sel && group.full_qty !== group.qty ? ` / ${group.full_qty}` : ""})
                </span>
              </div>
              <div className="text-xs text-[var(--smoke)]">
                {group.qty} sticker{group.qty === 1 ? "" : "s"}
              </div>
            </div>
          </div>
          <div className={totalCls} data-testid={`quote-category-subtotal-${group.key}`}>
            ${group.subtotal.toFixed(2)}
          </div>
        </button>
        {showSelectAll && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); sel.onSelectCategory(group.key); }}
            className="shrink-0 px-3 md:px-4 border-l-2 border-[var(--ink)] bg-white hover:bg-[var(--panini-yellow-soft)] transition inline-flex items-center gap-1.5 text-[10px] md:text-xs font-bold uppercase tracking-widest"
            data-testid={`quote-category-select-all-${group.key}`}
            aria-label={`Select all ${group.label} stickers`}
          >
            <CheckSquare size={12} /> Select all
          </button>
        )}
        {showUnselectAll && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); sel.onUnselectCategory(group.key); }}
            className="shrink-0 px-3 md:px-4 border-l-2 border-[var(--ink)] bg-white hover:bg-[var(--panini-red)]/10 transition inline-flex items-center gap-1.5 text-[10px] md:text-xs font-bold uppercase tracking-widest text-[var(--panini-red)]"
            data-testid={`quote-category-unselect-all-${group.key}`}
            aria-label={`Unselect all ${group.label} stickers`}
            data-indeterminate={groupIndeterminate ? "true" : "false"}
          >
            <Square size={12} /> Unselect all
          </button>
        )}
        {showRestoreButton && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); sel.onRestoreCategory(group.key); }}
            className="shrink-0 px-3 md:px-4 border-l-2 border-[var(--ink)] bg-white hover:bg-[var(--panini-yellow-soft)] transition inline-flex items-center gap-1.5 text-[10px] md:text-xs font-bold uppercase tracking-widest"
            data-testid={`quote-category-restore-${group.key}`}
            aria-label={`Restore all deselected ${group.label} stickers`}
          >
            <RotateCcw size={12} /> Restore
          </button>
        )}
      </div>
      {open && (
        <div
          className={linesWrapCls}
          data-testid={`quote-category-lines-${group.key}`}
        >
          {group.lines.map((ln, i) => {
            const selected = isRowSelected(ln.code);
            const removedRowCls = selected ? "" : " opacity-40";
            return (
            <div
              key={`${ln.code}-${i}`}
              className={rowCls + removedRowCls}
              data-testid={`quote-line-${ln.code}`}
              data-selected={selected ? "true" : "false"}
            >
              <span className="col-span-6 md:col-span-7 font-mono font-bold flex flex-wrap items-center gap-1.5 min-w-0">
                {sel && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); sel.onToggle(ln.code); }}
                    className="shrink-0 inline-flex items-center justify-center hover:text-[var(--panini-red)] transition"
                    aria-label={selected ? `Remove ${ln.code} from order` : `Restore ${ln.code} to order`}
                    aria-pressed={selected}
                    data-testid={`quote-line-checkbox-${ln.code}`}
                  >
                    {selected
                      ? <CheckSquare size={16} className="text-[var(--panini-red)]" />
                      : <Square size={16} className="text-[var(--smoke)]" />}
                  </button>
                )}
                <span className={"truncate" + (selected ? "" : " line-through")}>{ln.code}</span>
                {(ln.display_name || ln.player_name || ln.subject) && (
                  <span
                    className={"font-sans font-normal text-[11px] text-[var(--smoke)] truncate" + (selected ? "" : " line-through")}
                    title={ln.display_name || ln.player_name || ln.subject}
                    data-testid={`quote-line-name-${ln.code}`}
                  >
                    · {ln.display_name || ln.player_name || ln.subject}
                  </span>
                )}
                <AvailabilityPill availability={ln.availability} />
              </span>
              <span className="col-span-2 text-right text-[var(--smoke)] tabular-nums">
                ×{ln.qty}
              </span>
              <span className={"col-span-2 text-right font-mono text-[var(--smoke)] tabular-nums" + (selected ? "" : " line-through opacity-60")}>
                ${Number(ln.unit_price).toFixed(2)}
              </span>
              <span className={"col-span-2 text-right font-mono font-bold tabular-nums" + (selected ? "" : " line-through opacity-60")}>
                ${Number(ln.line_total).toFixed(2)}
              </span>
            </div>
          );})}
        </div>
      )}
    </div>
  );
}

export default function QuoteCategorySummary({ lines, groups: groupsProp, variant = "customer", orderVariant, selectionApi }) {
  const groups = useMemo(
    () => (groupsProp ? groupsProp : groupLinesByCategory(lines, selectionApi?.isSelected, orderVariant)),
    [lines, groupsProp, selectionApi, orderVariant]
  );
  if (groups.length === 0) return null;
  return (
    <div className={variant === "admin" ? "space-y-2" : "space-y-3"} data-testid="quote-category-summary">
      {groups.map((g) => (
        <CategoryBlock
          key={g.key}
          group={g}
          variant={variant}
          defaultOpen={g.lines.length <= 5}
          selectionApi={selectionApi}
        />
      ))}
    </div>
  );
}

// Exported for the DM-ready invoice text builder on the public quote.
export { OTHER_KEY, OTHER_LABEL };
